import { Helmet } from 'react-helmet';
import logo from '../logo.svg';
import judah from '../img/leadership/judah.png';
import jos from '../img/leadership/jos.png';
import jeba from '../img/leadership/jeba.png';

// NOTE: The original image imports pointed to local files.
// These have been replaced with placeholders.
// You can replace the `src` attribute with your actual image paths.
// const judah = 'https://placehold.co/400x400/F5F5F7/1D1D1F?text=Judah';
// const jeba = 'https://placehold.co/400x400/F5F5F7/1D1D1F?text=Jeba';
// const jos = 'https://placehold.co/400x400/F5F5F7/1D1D1F?text=Jos';
// const logo = 'https://placehold.co/800x200/F5F5F7/1D1D1F?text=PalmDigitalz';


// Main Component for the Apple-Inspired Company Page
const App = () => {

  // The CSS is embedded here for convenience and to keep the component self-contained.
  // It has been completely rewritten to match an Apple-like design system.
  const styles = `
    /* * FONT: Using 'Inter', a clean sans-serif font that is very similar to Apple's SF Pro.
     * In a real project, import this in your main index.html for best performance.
     */
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    /* CSS variables for consistent theming and easier maintenance (LIGHT MODE) */
    :root {
      --bg-color: #F5F5F7;
      --text-primary: #1D1D1F;
      --text-secondary: #6E6E73;
      --card-bg: rgba(255, 255, 255, 0.7);
      --card-border: rgba(0, 0, 0, 0.1);
      --accent-color: #007AFF;
      --font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }
    
    /* Global style to prevent white background on overscroll */
    html, body {
        background-color: var(--bg-color);
    }

    .apple-style-page {
      background-color: var(--bg-color);
      color: var(--text-primary);
      font-family: var(--font-family);
      position: relative;
      overflow-x: hidden;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    /* * APPLE INTELLIGENCE GRADIENT (Light Mode Version):
     * The gradient is now much softer and more subtle for the light background.
     * Opacity is reduced to create a gentle, ethereal glow.
     */
    .apple-style-page::before {
      content: '';
      position: fixed;
      width: 200%;
      height: 200%;
      top: -50%;
      left: -50%;
      z-index: 0;
      background: 
        radial-gradient(circle at 10% 20%, rgba(255, 149, 0, 0.08), transparent 40%),
        radial-gradient(circle at 80% 30%, rgba(0, 122, 255, 0.1), transparent 40%),
        radial-gradient(circle at 30% 80%, rgba(255, 45, 85, 0.08), transparent 40%),
        radial-gradient(circle at 90% 90%, rgba(52, 199, 89, 0.1), transparent 40%);
      animation: subtle-glow 40s ease-in-out infinite;
      will-change: transform;
    }

    @keyframes subtle-glow {
      0% { transform: rotate(0deg) scale(1); }
      50% { transform: rotate(180deg) scale(1.15); }
      100% { transform: rotate(360deg) scale(1); }
    }

    .apple-style-wrapper {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 1100px;
      padding: 10rem 2rem;
      margin: 0 auto;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 10rem;
    }
    
    .apple-style-section {
       text-align: center;
    }

    /* HERO SECTION */
    .apple-hero {
      padding: 4rem 0;
    }

    .apple-hero h1 {
      font-size: 6rem;
      font-weight: 800;
      text-align: center;
      letter-spacing: -0.04em;
      color: var(--text-primary);
      margin-bottom: 2rem;
      line-height: 1.1;
    }
    
    .apple-hero .logo-img {
      width: 100%;
      max-width: 400px;
      height: auto;
      margin: -10 auto 3rem;
    }

    .apple-hero p {
      font-size: 1.75rem;
      max-width: 700px;
      margin: 0 auto;
      color: var(--text-secondary);
      line-height: 1.6;
      font-weight: 500;
    }
    
    /* SECTION TITLE: Minimalist and centered */
    .section-title-apple {
        font-size: 3rem;
        font-weight: 700;
        letter-spacing: -0.03em;
        margin-bottom: 4rem;
        color: var(--text-primary);
    }
    
    /* MISSION & VISION: Clean, balanced layout */
    .mission-vision-grid-apple {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      text-align: left;
    }

    .mission-vision-grid-apple h3 {
      font-size: 1.5rem;
      font-weight: 700;
      color: var(--text-primary);
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    
    .mission-vision-grid-apple .icon {
        color: var(--accent-color);
    }

    .mission-vision-grid-apple .icon svg {
        width: 32px;
        height: 32px;
    }

    .mission-vision-grid-apple p {
        color: var(--text-secondary);
        font-size: 1.1rem;
        line-height: 1.6;
    }

    /* CORE VALUES: Frosted glass cards */
    .values-grid-apple {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 2rem;
    }
    
    .value-item-apple {
      background-color: var(--card-bg);
      border: 1px solid var(--card-border);
      padding: 2.5rem;
      border-radius: 24px;
      text-align: left;
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
    }

    .value-item-apple:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
    }

    .value-item-apple .value-icon {
      margin-bottom: 1.5rem;
    }

    .value-item-apple .value-icon svg {
       width: 36px;
       height: 36px;
       color: var(--text-primary);
    }

    .value-item-apple h4 {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 0.75rem;
      margin-left: 0px;
    }
    
    .value-item-apple p {
      color: var(--text-secondary);
      font-size: 1rem;
      line-height: 1.6;
    }

    /* TEAM SECTION: Soft, rounded visuals */
    .team-grid-apple {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 4rem;
    }
    
    .team-member-apple .photo {
        width: 180px;
        height: 180px;
        border-radius: 30%; /* Apple's "squircle" shape */
        margin: 0 auto 1.5rem;
        overflow: hidden;
        transition: transform 0.4s ease, border-radius 0.4s ease;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
    }
    
    .team-member-apple:hover .photo {
        transform: scale(1.05);
        border-radius: 40%;
    }

    .team-member-apple img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(0); /* Images start in color for light mode */
    }

    .team-member-apple h4 {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 0.25rem;
      margin-left: 0px;
    }
    
    .team-member-apple p {
      font-size: 1rem;
      color: var(--text-secondary);
      font-weight: 500;
      margin-bottom: 1rem;
    }
    
    /* Social Links for Team Members */
    .team-social-links {
      display: flex;
      justify-content: center;
      gap: 1.25rem;
      margin-top: 1rem;
    }

    .team-social-links a {
      color: var(--text-secondary);
      transition: color 0.3s ease, transform 0.3s ease;
    }
    
    .team-social-links a:hover {
      color: var(--accent-color);
      transform: scale(1.1);
    }

    .team-social-links svg {
      width: 24px;
      height: 24px;
    }
    
    /* CTA SECTION */
    .cta-section-apple {
      padding: 4rem 2rem;
      margin-top: 4rem;
      background-color: var(--card-bg);
      border: 1px solid var(--card-border);
      border-radius: 24px;
      backdrop-filter: blur(20px) saturate(180%);
      -webkit-backdrop-filter: blur(20px) saturate(180%);
    }

    .cta-section-apple h3 {
      font-size: 2rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      margin-bottom: 1.5rem;
      color: var(--text-primary);
    }

    .cta-button-apple {
      display: inline-block;
      background-color: var(--accent-color);
      color: #fff;
      font-size: 1.1rem;
      font-weight: 600;
      padding: 1rem 2.5rem;
      border-radius: 9999px; /* Pill shape */
      text-decoration: none;
      transition: transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease;
      box-shadow: 0 8px 20px rgba(0, 122, 255, 0.2);
    }

    .cta-button-apple:hover {
      transform: translateY(-4px);
      background-color: #0085FF;
      box-shadow: 0 12px 30px rgba(0, 122, 255, 0.3);
    }

    /* LOCATION SECTION: Simple and clean */
    .location-section-apple {
       padding-top: 4rem;
       border-top: 1px solid var(--card-border);
    }

    .location-section-apple p {
      font-size: 1.25rem;
      line-height: 1.7;
      max-width: 600px;
      margin: 0 auto;
      color: var(--text-secondary);
    }

    .location-section-apple strong {
      color: var(--text-primary);
      font-weight: 600;
    }
    
    /* FLOATING HOME BUTTON */
    .floating-home-btn {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        z-index: 1000;
        background-color: var(--card-bg);
        border: 1px solid var(--card-border);
        width: 60px;
        height: 60px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        text-decoration: none;
    }

    .floating-home-btn:hover {
        transform: scale(1.1);
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    }

    .floating-home-btn svg {
        width: 28px;
        height: 28px;
        color: var(--text-primary);
    }

    /* RESPONSIVE DESIGN */
    @media (max-width: 900px) {
        .team-grid-apple {
            grid-template-columns: 1fr;
            gap: 5rem;
        }
        .apple-hero h1 {
            font-size: 4.5rem;
        }
        .apple-hero p {
            font-size: 1.5rem;
        }
        .section-title-apple {
            font-size: 2.5rem;
        }
    }

    @media (max-width: 768px) {
        .apple-hero .logo-img {
            margin-top:-10rem;
        }
        .apple-style-wrapper {
            padding: 8rem 1.5rem;
            gap: 8rem;
        }
        .apple-hero h1 {
            font-size: 3.5rem;
        }
        .apple-hero p {
            font-size: 1.25rem;
        }
        .mission-vision-grid-apple {
            grid-template-columns: 1fr;
            gap: 4rem;
        }
        .floating-home-btn {
            width: 50px;
            height: 50px;
            bottom: 1.5rem;
            right: 1.5rem;
        }
        .floating-home-btn svg {
            width: 24px;
            height: 24px;
        }
    }
  `;

  return (
    <>
    <Helmet>
        <meta charSet="utf-8" />
        <title>The Legacy Modernization Company - PalmDigitalz</title>
        <link rel="canonical" href="http://palmdigitalz.com/company" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="description" content="About the Legacy Modernization Company, PalmDigitalz" />
        <meta name="keywords" content="mainframe modernization, legacy modernization, app modernization, app rationalization, insight extraction, forward engineering, code generation" />
        <link rel="icon" href={logo}></link>
    </Helmet>

      <style>{styles}</style>
      <div className="apple-style-page">
        <main className="apple-style-wrapper">
        
          <header className="apple-hero apple-style-section">
            <img src={logo} alt="PalmDigitalz Logo" className="logo-img" height="100px"/>
            <h1>PalmDigitalz</h1>
            <p>Leading legacy systems into the future.</p>
          </header>

          <section className="apple-style-section">
            <h2 className="section-title-apple">Our Directive</h2>
            <div className="mission-vision-grid-apple">
              <div className="item">
                <h3>
                  <div className="icon">
                     {/* Icon: Sun for Mission */}
                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" /></svg>
                  </div>
                  Our Mission
                </h3>
                <p>To help organizations bleeding from heavy licensing costs, rigid systems and retired/dead programmers to code on a legacy app.</p>
              </div>
              <div className="item">
                <h3>
                  <div className="icon">
                     {/* Icon: Star for Vision */}
                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
                  </div>
                  Our Vision
                </h3>
                <p>A world where we've successfully helped mission-critical systems like space, banking, finance and government to be rid of any legacy system.</p>
              </div>
            </div>
          </section>

          <section className="apple-style-section">
            <h2 className="section-title-apple">Core Values</h2>
            <div className="values-grid-apple">
              <div className="value-item-apple">
                <div className="value-icon">
                  {/* Icon: Chevrons for Momentum */}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                </div>
                <h4>Obsessive Momentum</h4>
                <p>We believe in sustained, relentless progress. Every action is a step forward, building on the last to create unstoppable velocity.</p>
              </div>
              <div className="value-item-apple">
                <div className="value-icon">
                   {/* Icon: Lightning for Speed */}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                </div>
                <h4>Move at the Speed of Light</h4>
                <p>In a fast-changing world, speed is a strategy. We prioritize rapid iteration, decisive action, and efficient execution.</p>
              </div>
              <div className="value-item-apple">
                <div className="value-icon">
                   {/* Icon: Heart for Value */}
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" /></svg>
                </div>
                <h4>It's All About the Value</h4>
                <p>Our success is measured by the tangible value we deliver to our customers. We are obsessed with creating meaningful impact.</p>
              </div>
            </div>
          </section>

          <section className="apple-style-section">
            <h2 className="section-title-apple">Our Leadership</h2>
            <div className="team-grid-apple">
              <div className="team-member-apple">
                <div className="photo">
                  <img src={jeba} alt="Elizabeth, Jeba" />
                </div>
                <h4>Jeba Elizabeth</h4>
                <p>Founder & CEO</p>
              </div>
              <div className="team-member-apple">
                <div className="photo">
                  <img src={jos} alt="Gnanasigamoni, Jos Jacinth" />
                </div>
                <h4>Jos Jacinth Gnanasigamoni</h4>
                <p>Founder & CTO</p>
                <div className="team-social-links">
                    <a href="https://www.linkedin.com/in/josjacinthgnanasigamoni/" target="_blank" rel="noopener noreferrer" aria-label="Jos's LinkedIn">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.62 1.62 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path></svg>
                    </a>
                    <a href="mailto:jos@palmdigitalz.com" aria-label="Email Jos">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                    </a>
                </div>
              </div>
              <div className="team-member-apple">
                <div className="photo">
                  <img src={judah} alt="Jos Jacinth, Judah" />
                </div>
                <h4>Judah Jos Jacinth</h4>
                <p>Technologist</p>
                <div className="team-social-links">
                    <a href="https://www.linkedin.com/in/judahjacinth/" target="_blank" rel="noopener noreferrer" aria-label="Judah's LinkedIn">
                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.25 6.5 1.75 1.75 0 016.5 8.25zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.62 1.62 0 0013 14.19V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path></svg>
                    </a>
                    <a href="mailto:judah@palmdigitalz.com" aria-label="Email Judah">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
                    </a>
                </div>
              </div>
            </div>
          </section>

          <section className="cta-section-apple apple-style-section">
            <h3>Ready to Modernize Your Systems?</h3>
            <a href="https://outlook.office.com/bookwithme/user/536ba4d9fc8b4115b7724c68f552d248@palmdigitalz.com?anonymous&ep=plink" target="_blank" rel="noopener noreferrer" className="cta-button-apple">
              Book a 1-on-1 with Judah
            </a>
          </section>

          <section className="location-section-apple apple-style-section">
            <p>
              <strong>PalmDigitalz</strong><br/>
              Falcon's Nest, 35/A, Anandhapuram<br/>
              Annai Theresa Street, East Tambaram<br/>
              Chennai, Tamil Nadu 600059
            </p>
          </section>

        </main>
        
        <a href="/" className="floating-home-btn" aria-label="Go to Homepage">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
        </a>
      </div>
    </>
  );
};

export default App;
